<div class="p-8 max-w-7xl mx-auto" *ngIf="run">

  <!-- Header -->
  <div class="flex items-start justify-between mb-8 gap-6 flex-wrap">
    <div class="flex items-start gap-4">
      <button (click)="goBack()" class="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition">
        <i class="fas fa-arrow-left mr-1"></i>Volver
      </button>
      <div>
        <h1 class="text-2xl font-bold text-gray-800 leading-tight">{{ run.job_label || run.job_key }}</h1>
        <div class="text-xs text-gray-500 mt-2 leading-relaxed">
          <span class="inline-block px-2 py-0.5 bg-gray-100 rounded mr-2">{{ run.project }}</span>
          <span class="font-mono mr-2">{{ run.job_key }}</span>
          <span class="text-gray-400">Run ID:</span>
          <span class="font-mono text-gray-600">{{ run._id }}</span>
        </div>
      </div>
    </div>
    <div class="flex gap-3 flex-shrink-0">
      <button *ngIf="run.status === 'running' && !run.cancel_requested"
              (click)="cancel()" [disabled]="cancelling"
              class="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-md text-sm font-medium shadow-sm transition">
        <i class="fas fa-stop mr-2"></i>Cancelar
      </button>
      <button (click)="downloadXlsx()"
              class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium shadow-sm transition">
        <i class="fas fa-file-excel mr-2"></i>Descargar XLSX
      </button>
      <button (click)="deleteRun()"
              [disabled]="deleting || (run.status === 'running' && !run.cancel_requested)"
              [title]="run.status === 'running' && !run.cancel_requested ? 'Cancelá primero para poder eliminar' : 'Eliminar corrida'"
              class="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-md text-sm font-medium transition">
        <i class="fas fa-trash mr-2" *ngIf="!deleting"></i>
        <i class="fas fa-spinner fa-spin mr-2" *ngIf="deleting"></i>
        {{ deleting ? 'Eliminando...' : 'Eliminar' }}
      </button>
    </div>
  </div>

  <!-- Status + progress card -->
  <div class="bg-white border border-gray-200 rounded-xl p-8 mb-6 shadow-sm">
    <div class="flex items-center justify-between mb-6 flex-wrap gap-3">
      <div class="flex items-center gap-4">
        <span class="px-4 py-1.5 text-sm font-medium rounded-full" [ngClass]="statusBadgeClass(run.status)">
          {{ run.status }}
        </span>
        <span *ngIf="run.cancel_requested" class="text-orange-600 text-sm font-medium">⏹ Cancelación solicitada</span>
        <span *ngIf="run.stale" class="text-red-600 text-sm font-medium">⚠ Heartbeat sin update (posible cuelgue)</span>
      </div>
      <div class="text-sm text-gray-600 flex gap-5">
        <span><i class="fas fa-clock mr-2 text-gray-400"></i>Duración: <strong class="text-gray-800">{{ fmtDuration(run.duration_ms) }}</strong></span>
        <span *ngIf="run.status === 'running'"><i class="fas fa-hourglass-half mr-2 text-gray-400"></i>ETA: <strong class="text-gray-800">{{ etaText() }}</strong></span>
      </div>
    </div>

    <!-- Progress bar -->
    <div class="mb-3">
      <div class="flex items-center justify-between text-sm text-gray-600 mb-1">
        <span>Progreso</span>
        <span>{{ run.progress?.processed || 0 }} / {{ run.progress?.total || 0 }} ({{ progressPct() }}%)</span>
      </div>
      <div class="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
        <div class="h-full transition-all"
             [style.width.%]="progressPct()"
             [class.bg-blue-500]="run.status === 'running'"
             [class.bg-green-500]="run.status === 'success'"
             [class.bg-yellow-500]="run.status === 'partial'"
             [class.bg-red-500]="run.status === 'failed'"
             [class.bg-gray-400]="run.status === 'cancelled'"></div>
      </div>
    </div>

    <!-- Counters -->
    <div class="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
      <div class="bg-blue-50 border border-blue-100 rounded-lg p-5 text-center">
        <div class="text-3xl font-bold text-blue-700 leading-tight">{{ run.progress?.processed || 0 }}</div>
        <div class="text-xs font-medium text-blue-600 mt-2 uppercase tracking-wide">Procesados</div>
      </div>
      <div class="bg-green-50 border border-green-100 rounded-lg p-5 text-center">
        <div class="text-3xl font-bold text-green-700 leading-tight">{{ run.progress?.ok || 0 }}</div>
        <div class="text-xs font-medium text-green-600 mt-2 uppercase tracking-wide">OK</div>
      </div>
      <div class="bg-red-50 border border-red-100 rounded-lg p-5 text-center">
        <div class="text-3xl font-bold text-red-700 leading-tight">{{ run.progress?.failed || 0 }}</div>
        <div class="text-xs font-medium text-red-600 mt-2 uppercase tracking-wide">Fallidos</div>
      </div>
      <div class="bg-yellow-50 border border-yellow-100 rounded-lg p-5 text-center">
        <div class="text-3xl font-bold text-yellow-700 leading-tight">{{ run.progress?.skipped || 0 }}</div>
        <div class="text-xs font-medium text-yellow-600 mt-2 uppercase tracking-wide">Omitidos</div>
      </div>
      <div class="bg-gray-50 border border-gray-100 rounded-lg p-5 text-center">
        <div class="text-3xl font-bold text-gray-700 leading-tight">{{ run.progress?.current_batch || 0 }}</div>
        <div class="text-xs font-medium text-gray-600 mt-2 uppercase tracking-wide">Lote actual</div>
      </div>
    </div>
  </div>

  <!-- Meta info -->
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
    <div class="bg-white border border-gray-200 rounded-lg p-4">
      <h3 class="font-semibold text-gray-800 mb-2 text-sm">Trigger</h3>
      <div class="text-sm text-gray-600 space-y-1">
        <div>Tipo: <span class="font-medium">{{ run.trigger }}</span></div>
        <div *ngIf="run.triggered_by?.email">Usuario: <span class="font-medium">{{ run.triggered_by.email }}</span></div>
        <div *ngIf="run.triggered_by?.internal_client">Cliente interno: <span class="font-medium">{{ run.triggered_by.internal_client }}</span></div>
        <div *ngIf="run.triggered_by?.source">Source: <span class="font-medium">{{ run.triggered_by.source }}</span></div>
      </div>
    </div>
    <div class="bg-white border border-gray-200 rounded-lg p-4">
      <h3 class="font-semibold text-gray-800 mb-2 text-sm">Tiempos</h3>
      <div class="text-sm text-gray-600 space-y-1">
        <div>Inicio: <span class="font-medium">{{ run.started_at | date:'dd/MM/yyyy HH:mm:ss' }}</span></div>
        <div *ngIf="run.finished_at">Fin: <span class="font-medium">{{ run.finished_at | date:'dd/MM/yyyy HH:mm:ss' }}</span></div>
        <div *ngIf="run.heartbeat_at">Último heartbeat: <span class="font-medium">{{ run.heartbeat_at | date:'HH:mm:ss' }}</span></div>
      </div>
    </div>
  </div>

  <!-- Summary -->
  <div *ngIf="summaryEntries().length > 0" class="bg-white border border-gray-200 rounded-lg p-4 mb-4">
    <h3 class="font-semibold text-gray-800 mb-3 text-sm">Resumen</h3>
    <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div *ngFor="let entry of summaryEntries()" class="bg-gray-50 rounded p-3">
        <div class="text-xs text-gray-500">{{ entry[0] }}</div>
        <div class="text-sm font-semibold text-gray-800">{{ formatSummaryValue(entry[1]) }}</div>
      </div>
    </div>
  </div>

  <!-- Error -->
  <div *ngIf="run.error_message" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
    <h3 class="font-semibold text-red-800 mb-1 text-sm">Error</h3>
    <pre class="text-sm text-red-700 whitespace-pre-wrap">{{ run.error_message }}</pre>
  </div>

  <!-- Tabs: Log + Items -->
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

    <!-- Log live -->
    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h3 class="font-semibold text-gray-800 text-sm">
          Log en vivo <span class="text-xs text-gray-500">({{ eventsTotal }})</span>
        </h3>
        <select [(ngModel)]="eventsLevelFilter" (change)="loadEvents()" class="border rounded text-xs px-2 py-1">
          <option value="">Todos</option>
          <option value="info">Info</option>
          <option value="warn">Warn</option>
          <option value="error">Error</option>
        </select>
      </div>
      <div class="h-96 overflow-y-auto p-3 text-xs font-mono space-y-1 bg-gray-900 text-gray-100">
        <div *ngIf="events.length === 0" class="text-gray-500 italic">Sin eventos aún…</div>
        <div *ngFor="let ev of events" class="flex gap-2">
          <span class="text-gray-400 flex-shrink-0">{{ ev.at | date:'HH:mm:ss' }}</span>
          <span class="px-1.5 py-0.5 rounded text-[10px] uppercase flex-shrink-0"
                [class.bg-red-700]="ev.level === 'error'"
                [class.bg-yellow-700]="ev.level === 'warn'"
                [class.bg-blue-700]="ev.level === 'info'">{{ ev.level }}</span>
          <span class="text-gray-100 break-all">{{ ev.msg }}</span>
        </div>
      </div>
    </div>

    <!-- Items -->
    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h3 class="font-semibold text-gray-800 text-sm">
          Items procesados <span class="text-xs text-gray-500">({{ itemsTotal }})</span>
        </h3>
        <select [(ngModel)]="itemsResultFilter" (change)="loadItems()" class="border rounded text-xs px-2 py-1">
          <option value="">Todos</option>
          <option value="ok">OK</option>
          <option value="failed">Fallidos</option>
          <option value="skipped">Omitidos</option>
          <option value="noop">Sin cambio</option>
        </select>
      </div>
      <div class="h-96 overflow-auto">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th *ngFor="let col of getItemColumns()" class="px-3 py-2 text-left text-gray-600 uppercase">{{ col.header }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr *ngIf="items.length === 0 && !loadingItems">
              <td [colSpan]="getItemColumns().length" class="px-3 py-6 text-center text-gray-500">Sin items aún</td>
            </tr>
            <tr *ngFor="let item of items">
              <td *ngFor="let col of getItemColumns()" class="px-3 py-2 text-gray-700">
                <span *ngIf="col.key === 'result'; else regular"
                      class="px-2 py-0.5 rounded-full text-[10px]"
                      [ngClass]="resultBadge(getColumnValue(item, col.key))">
                  {{ getColumnValue(item, col.key) }}
                </span>
                <ng-template #regular>{{ getColumnValue(item, col.key) }}</ng-template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- Error global -->
  <div *ngIf="error" class="bg-red-50 border border-red-200 text-red-700 rounded p-3 mt-4 text-sm">
    {{ error }}
  </div>
</div>

<div *ngIf="!run" class="p-6 text-center text-gray-500">Cargando…</div>
